<template>
  <div
    v-if="isVisible"
    :class="[
      'own-dialog',
      'flex-col',
      `own-dialog--height-${height}`,
      { 'own-dialog--anchored': anchored },
    ]"
    @click.stop="onCancel"
  >
    <OwnCard
      v-motion-slide-visible-bottom
      :class="[
        'own-dialog__card',
        `own-dialog__card--size-${size}`,
        `own-dialog__card--height-${height}`,
        `own-dialog__card--position-${position}`,
      ]"
      :radius="cardRadius"
      @click.stop
    >
      <slot name="full-dialog">
        <slot name="heading" :close="closeDialog" />
        <div v-if="height === 'full'" class="own-dialog__card-content">
          <slot :close="closeDialog" />
        </div>
        <slot v-else :close="closeDialog" />
        <slot name="footer" :close="closeDialog" />
      </slot>
    </OwnCard>
  </div>
</template>

<script lang="ts" setup>
import { computed, provide, toRef } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'

import { OwnCard } from '../OwnCard'

export type DialogSize = 'sm' | 'lg'
export type DialogHeight = 'auto' | 'full'
export type DialogPosition = 'center' | 'right'

const props = withDefaults(
  defineProps<{
    anchored?: boolean
    height?: DialogHeight
    navigationGuard?: boolean
    persistent?: boolean
    position?: DialogPosition
    size?: DialogSize
  }>(),
  {
    anchored: false,
    height: 'auto',
    navigationGuard: false,
    persistent: false,
    position: 'center',
    size: 'sm',
  }
)

// Provide the persistent state as a reactive reference for child components
provide('dialogPersistent', toRef(props, 'persistent'))

onBeforeRouteLeave((to, from) => {
  if (!props.navigationGuard) return

  const answer = window.confirm('Do you really want to leave?')
  if (!answer) return false
})

const emit = defineEmits<{ cancel: [] }>()

const isVisible = defineModel<boolean>()

const cardRadius = computed(() => {
  if (props.anchored && props.position === 'right') {
    return 'var(--radius-modal-regular) 0 0 var(--radius-modal-regular)'
  }

  return undefined
})

const closeDialog = () => {
  isVisible.value = false
}

const onCancel = () => {
  if (!props.persistent) {
    emit('cancel')
    closeDialog()
  }
}
</script>

<style lang="scss">
@use '@/styles/breakpoints';

.own-dialog {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 300;

  background-color: rgba(0, 0, 0, 0.89);
  padding: 16px;
  margin: 0;

  &--anchored {
    padding: 0;
  }

  &--height-auto {
    justify-content: center;
  }

  &__card {
    width: 100%;

    &--position-center {
      align-self: center;
    }
    &--position-right {
      align-self: flex-end;
    }

    &--height-auto {
      max-height: calc(100% - 80px);
    }
    &--height-full {
      height: 100%;
      max-height: 100%;
      justify-content: flex-start;

      .own-dialog__card-content {
        flex: 1;
        overflow-y: auto;
      }
    }

    &--size-lg {
      @include breakpoints.respond-to('lg-and-up') {
        width: 900px;
      }
    }
    &--size-sm {
      @include breakpoints.respond-to('md-and-up') {
        width: 600px;
      }
    }
  }

  &__close {
    width: 20px;
    height: 20px;
  }
}
</style>
